import { ReactNode } from 'react';
import { Trans } from 'react-i18next';

// Material UI Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import URLS from 'URLS';

const HeaderText = styled(Typography)(({ theme }) => ({
  wordWrap: 'break-word',
  margin: 0,
  paddingBottom: theme.spacing(5),
  fontSize: '1.25rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.5rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.75rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '2rem',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '2.5rem',
  },
}));

const HeaderTextNoImage = styled(Typography)(({ theme }) => ({
  wordWrap: 'break-word',
  textAlign: 'center',
  margin: 0,
  paddingBottom: theme.spacing(1),
  fontSize: '1rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.125rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.25rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.375rem',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '1.75rem',
  },
}));

const BodyText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  whiteSpace: 'pre-line',
  [theme.breakpoints.up('sm')]: {
    fontSize: '0.9375rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.125rem',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '1.25rem',
  },
}));

export type PosterProps = {
  header?: string;
  text?: string;
  image?: string;
};

const Mailto = ({ children }: { children?: ReactNode }) => <a href={`mailto:${children}`}>{children}</a>;

const Calendly = ({ children }: { children?: ReactNode }) => (
  <a href={URLS.CALENDLY} rel='noopener noreferrer' target='_blank'>
    {children}
  </a>
);

const Poster = ({ header, text, image }: PosterProps) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'row',
          width: '90%',
          gap: theme.spacing(5),
          [theme.breakpoints.down('lg')]: {
            flexDirection: 'column',
            gap: theme.spacing(3),
          },
        })}>
        {(header || text) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {header && image && <HeaderText variant='h1'>{header}</HeaderText>}
            {header && !image && <HeaderTextNoImage variant='h1'>{header}</HeaderTextNoImage>}
            {text && (
              <BodyText>
                <Trans components={{ mailto: <Mailto />, bold: <strong />, calendly: <Calendly /> }}>{text}</Trans>
              </BodyText>
            )}
          </Box>
        )}
        {image && (
          <Box
            component='img'
            src={image}
            sx={(theme) => ({
              maxWidth: '50%',
              objectFit: 'contain',
              [theme.breakpoints.down('lg')]: {
                order: -1,
                maxWidth: '100%',
              },
            })}
          />
        )}
      </Box>
    </Box>
  );
};

export default Poster;
